// frontend/src/components/portfolio/MediaLibrary.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardActions,
  IconButton,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  TextField
} from '@mui/material';
import {
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useMediaLibrary } from '../../hooks/useMediaLibrary';

// Helper function to format file size
const formatFileSize = (bytes?: number): string => {
  if (!bytes) return 'Unknown';
  
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  if (bytes === 0) return '0 Bytes';
  
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

const MediaLibrary: React.FC = () => {
  const {
    assets,
    loading,
    uploading,
    error,
    total,
    page,
    totalPages,
    uploadFile,
    deleteAsset,
    refresh,
    loadMore
  } = useMediaLibrary();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<{ id: string; filename: string } | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadMetadata, setUploadMetadata] = useState({ altText: '', tags: '' });

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const metadata: any = {};
        if (uploadMetadata.altText) metadata.altText = uploadMetadata.altText;
        if (uploadMetadata.tags) metadata.tags = uploadMetadata.tags.split(',').map(t => t.trim());
        
        await uploadFile(file, metadata);
        setUploadMetadata({ altText: '', tags: '' }); // Reset
      } catch (err) {
        // Error already handled in hook
      }
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const metadata: any = {};
        if (uploadMetadata.altText) metadata.altText = uploadMetadata.altText;
        if (uploadMetadata.tags) metadata.tags = uploadMetadata.tags.split(',').map(t => t.trim());
        
        await uploadFile(file, metadata);
        setUploadMetadata({ altText: '', tags: '' }); // Reset
      } catch (err) {
        // Error already handled in hook
      }
    }
  };

  const handleDeleteClick = (id: string, filename: string) => {
    setAssetToDelete({ id, filename });
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (assetToDelete) {
      try {
        await deleteAsset(assetToDelete.id);
      } catch (err) {
        // Error already handled in hook
      }
    }
    setDeleteDialogOpen(false);
    setAssetToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setAssetToDelete(null);
  };

  // Loading state
  if (loading && assets.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Media Library
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {total} {total === 1 ? 'image' : 'images'} total
          </Typography>
        </Box>
      </Box>

      {/* Search Bar */}
      <Box mb={2}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by filename, tags, or alt text..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ bgcolor: 'background.paper' }}
        />
      </Box>

      {/* Upload Form */}
      <Box mb={2} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
          Upload New Image
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              label="Alt Text"
              placeholder="Describe the image..."
              value={uploadMetadata.altText}
              onChange={(e) => setUploadMetadata(prev => ({ ...prev, altText: e.target.value }))}
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <TextField
              size="small"
              label="Tags"
              placeholder="portrait, headshot, press"
              value={uploadMetadata.tags}
              onChange={(e) => setUploadMetadata(prev => ({ ...prev, tags: e.target.value }))}
              helperText="Comma-separated"
              fullWidth
            />
          </Grid>
          
          <Grid item xs={12} sm={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleUploadClick}
              disabled={uploading}
              sx={{ height: '40px' }}
            >
              {uploading ? 'Uploading...' : 'Choose File'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => {}}>
          {error}
        </Alert>
      )}

      {/* Drag & Drop Area */}
      <Box
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        sx={{
          border: dragActive ? '2px dashed #1976d2' : '2px dashed transparent',
          borderRadius: 2,
          bgcolor: dragActive ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
          transition: 'all 0.3s',
          minHeight: '400px',
          p: 2
        }}
      >
        {/* Empty state */}
        {assets.length === 0 && !loading && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight="300px"
            gap={2}
          >
            <ImageIcon sx={{ fontSize: 80, color: 'text.disabled' }} />
            <Typography variant="h6" color="text.secondary">
              No images yet
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload your first image or drag & drop files here
            </Typography>
            <Button variant="outlined" onClick={handleUploadClick}>
              Upload Image
            </Button>
          </Box>
        )}

        {/* Image Grid */}
        {assets.length > 0 && (
          <Grid container spacing={2}>
            {assets
              .filter(asset => {
                if (!searchQuery) return true;
                const query = searchQuery.toLowerCase();
                return (
                  asset.filename.toLowerCase().includes(query) ||
                  asset.altText?.toLowerCase().includes(query) ||
                  asset.tags?.some(tag => tag.toLowerCase().includes(query))
                );
              })
              .map((asset) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={asset.id}>
                <Card sx={{ position: 'relative' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={asset.url}
                    alt={asset.altText || asset.filename}
                    sx={{ objectFit: 'cover' }}
                  />
                  <CardActions sx={{ flexDirection: 'column', alignItems: 'flex-start', px: 2, pb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mb: 1 }}>
                      <Typography
                        variant="caption"
                        noWrap
                        sx={{ flex: 1, mr: 1 }}
                        title={asset.filename}
                      >
                        {asset.filename}
                      </Typography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(asset.id, asset.filename)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    
                    {/* Alt Text */}
                    {asset.altText && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ display: 'block', fontStyle: 'italic', mb: 0.5, width: '100%' }}
                        noWrap
                        title={asset.altText}
                      >
                        {asset.altText}
                      </Typography>
                    )}
                    
                    {/* Tags */}
                    {asset.tags && asset.tags.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5, width: '100%' }}>
                        {asset.tags.slice(0, 3).map((tag, idx) => (
                          <Typography
                            key={idx}
                            variant="caption"
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              px: 0.5,
                              py: 0.25,
                              borderRadius: 0.5,
                              fontSize: '0.65rem'
                            }}
                          >
                            {tag}
                          </Typography>
                        ))}
                        {asset.tags.length > 3 && (
                          <Typography variant="caption" color="text.secondary">
                            +{asset.tags.length - 3}
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Metadata */}
                    <Box sx={{ width: '100%' }}>
                      {asset.metadata?.width && asset.metadata?.height && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {asset.metadata.width} × {asset.metadata.height}
                        </Typography>
                      )}
                      {asset.metadata?.size && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {formatFileSize(asset.metadata.size)}
                        </Typography>
                      )}
                      {asset.metadata?.format && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textTransform: 'uppercase' }}>
                          {asset.metadata.format}
                        </Typography>
                      )}
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Load More Button */}
        {page < totalPages && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="outlined"
              onClick={loadMore}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More'}
            </Button>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete}>
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{assetToDelete?.filename}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MediaLibrary;